import { UIElement } from '../ui-element';
import { FormValidator } from '../../global/form-validator';
import { getData, setData } from '../../global/ui-helpers';
import { Component } from '../../decorators/component.decorator';
import { Attribute } from '../../decorators/attribute.decorator';
import { validationLabels } from './validation-labels';
import type { UIFormLayouts, UIFormLayout } from './ui-form.type';
import type { AttributeValue, ObservedAttributes } from '../ui-element.type';
import type { ElementControlsData, NullOr } from '../../global/utils.type';
import type { UIField } from '../field/ui-field';
import type { UIMessageBox } from '../message/ui-messagebox';
import type { TranslationLabels } from '../../types/labels.type';
import styles from './ui-form.css';

/**
 * @class
 * @memberof SharedComponents
 * @augments {UIElement}
 * @alias UIForm
 * @element ui-form
 * @classdesc Represents a class for <code>ui-form</code> element.
 * Wrapper for native HTML <b>&lt;form></b> which extends form
 * native form functionality.
 * @fires event:form-update
 * @listens event:reset
 * @property {("default" | "inline" | "fluid")} layout {@attr layout} Layout of the ui-form.
 *  {@desc default}
 *  {@desc inline}
 *  {@desc fluid}
 * @property {string} form {@attr form} ID of the native HTML form that
 * ui-form will be linked with.
 * @property {string} novalidate {@attr novalidate} Same as 'novalidate in native form'.
 * @slot
 * @example
 * <ui-form><form></form></ui-form>
 * @example
 * <ui-form><ui-field label="Field"><input name="field" type="text" /></ui-field></ui-form>
 * @example
 * <ui-form form="my-form"></ui-form>
 * <form id="form-to-validate"></form>
 */
@Component({ tag: 'ui-form', styles })
export class UIForm extends UIElement {
    static readonly Layout: UIFormLayouts = {
        Default: 'default',
        Inline: 'inline',
        Fluid: 'fluid',
    };

    /**
     * Define labels what could be localised
     * @type {Labels}
     * @readonly
     */
    static get validationLabels(): TranslationLabels.Labels {
        return validationLabels;
    }

    /**
     * @type {string[]}
     */
    static get observedAttributes(): ObservedAttributes {
        return ['layout'];
    }

    @Attribute(String) layout?: UIFormLayout;
    @Attribute(String) form?: string;

    public validator?: NullOr<FormValidator>;
    private _validator: NullOr<FormValidator> = null;

    /**
     * Clears field errors when the form is reset.
     * @private
     */
    private handleReset = (): void => {
        Array.from(this.querySelectorAll<UIField>('ui-field')).forEach(
            (field) => {
                field.removeErrors?.();
            }
        );
    };

    /**
     * Finds or creates form element.
     * @returns {HTMLFormElement}
     */
    getFormElement(): HTMLFormElement {
        let externalForm: NullOr<HTMLFormElement> = null;
        if (this.form) {
            const foundForm = document.getElementById(this.form);
            externalForm =
                foundForm instanceof HTMLFormElement ? foundForm : null;
        }
        return externalForm
            ? externalForm
            : this.querySelector<HTMLFormElement>('form') ||
                  this.createFormElement();
    }

    /**
     * Finds or creates form element.
     * @returns {FormData}
     */
    getFormData(): FormData {
        return new FormData(this.getFormElement());
    }

    /**
     * Serializes form and returns data as an object.
     * Invert procedure is setDate().
     * @returns {ElementControlsData}
     */
    getData(): ElementControlsData {
        return getData(this.getFormElement());
    }

    /**
     * Sets the date to form from object. Invert procedure is getDate().
     * @param {object} data
     * @fires event:form-update
     * @returns {UIForm}
     */
    setData(data: Record<string, string>): this {
        setData(this.getFormElement(), data);
        return this;
    }

    /**
     * of element matched by selector.
     * @param {string} selector
     * @returns {HTMLElement | null}
     */
    getFieldGroup(selector: string): NullOr<UIField> {
        const foundElem = this.querySelector<HTMLElement>(selector);
        return foundElem ? foundElem.closest<UIField>('ui-field') : null;
    }

    /**
     * Finds input group either matched by selector or parent of element matched by selector.
     * @param {HTMLElement} element
     * @returns {HTMLElement | null}
     */
    getFieldGroupByElement(element: HTMLElement): NullOr<UIField> {
        return element.closest<UIField>('ui-field');
    }

    /**
     * Finds or creates a message box.
     * @returns {UIMessageBox}
     */
    getMessageBox(): HTMLElement {
        return (
            this.querySelector<UIMessageBox>('ui-messagebox') ||
            this.createMessageBox()
        );
    }

    /**
     * Renders child nodes and creates a form element.
     * @returns {HTMLFormElement}
     */
    createFormElement(): HTMLFormElement {
        const currentForm = this.querySelector<HTMLFormElement>('form');
        if (currentForm) {
            return currentForm;
        }

        const formElement = this.createElement({
            tagName: 'form',
            attributes: {
                method: this.getAttribute('method') || 'get',
                autocomplete: this.getAttribute('autocomplete') || 'off',
                action: this.getAttribute('action') || '#',
                novalidate: this.hasAttribute('novalidate'),
            },
            children: this.childNodes,
        }) as HTMLFormElement;
        this.appendChild(formElement);
        return formElement;
    }

    /**
     * Creates message box.
     * @returns {UIMessageBox}
     */
    createMessageBox(): UIMessageBox {
        const form = this.getFormElement();
        const box = document.createElement('ui-messagebox') as UIMessageBox;
        form.insertBefore(box, form.firstChild);
        return box;
    }

    /**
     * Show target field group matched by selector.
     * @param {string} selector
     * @returns {UIForm}
     */
    showFieldGroup(selector: string): this {
        const group = this.getFieldGroup(selector);
        if (group) {
            group.show?.();
        }
        return this;
    }

    /**
     * Hide target field group matched by selector.
     * @param {string} selector
     * @returns {UIForm}
     */
    hideFieldGroup(selector: string): UIForm {
        const group = this.getFieldGroup(selector);
        if (group) {
            group.hide?.();
        }
        return this;
    }

    /**
     * Unify layout type for all input group.
     */
    onLayoutUpdated(): void {
        const layout = this.layout;
        if (!layout) {
            return;
        }

        this.querySelectorAll('ui-field, ui-buttonbar').forEach((node) =>
            node.setAttribute('layout', layout)
        );
    }

    /**
     * @inheritDoc
     */
    observeAttributes(
        name: AttributeValue,
        oldValue: AttributeValue,
        newValue: NullOr<string>
    ): void {
        /* istanbul ignore if */
        if (!this.hydrated) {
            return;
        }
        switch (name) {
            case 'layout':
                this.onLayoutUpdated();
                break;
            /* istanbul ignore next */
            default:
                break;
        }
    }

    /**
     * Get all elements which could be validated.
     * @returns {HTMLFormControlsCollection}
     */
    getAllFormControls(): HTMLFormControlsCollection {
        return this.getFormElement().elements;
    }

    /**
     * Get submit control like button or input.
     * @returns {HTMLButtonElement | HTMLInputElement | null}
     */
    getSubmitControl(): NullOr<HTMLButtonElement | HTMLInputElement> {
        return this.getFormElement().querySelector<
            HTMLButtonElement | HTMLInputElement
        >('[type="submit"]');
    }

    /**
     * Attaches validator to current form.
     * @param {FormValidator} ValidatorClass
     */
    setValidator(ValidatorClass: typeof FormValidator): void {
        this._validator = new ValidatorClass(this.getFormElement());
    }

    /**
     * Gets the validator object.
     * @returns {FormValidator | null}
     */
    getValidator(): NullOr<FormValidator> {
        return this._validator;
    }

    /**
     * Removes validator from form.
     */
    removeValidator(): void {
        this._validator = null;
    }

    /** @private */
    private subscribeEvents(): void {
        this.addEventListener('reset', this.handleReset);
    }

    /** @private */
    private unsubscribeEvents(): void {
        this.removeEventListener('reset', this.handleReset);
    }

    /**
     * @inheritDoc
     */
    override render(): void {
        if (!this.form) {
            this.createFormElement();
        }
        this.onLayoutUpdated();
    }

    /**
     * @inheritDoc
     */
    override hydrate(): void {
        this.setValidator(FormValidator);
        this.subscribeEvents();
    }

    /**
     * @inheritDoc
     */
    override disconnect(): void {
        this.unsubscribeEvents();
    }

    /**
     * @inheritDoc
     */
    override reconnect(): void {
        this.subscribeEvents();
    }
}
